import { faArrowDownToSquare, faArrowUpFromSquare } from "@fortawesome/pro-duotone-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Button, ButtonGroup } from "@progress/kendo-react-buttons";
import { Grid, GridColumn, GridToolbar } from "@progress/kendo-react-grid";
import { Popup } from "@progress/kendo-react-popup";
import React from "react";
import InvalidParameterFormatDialog from "../../../Notification/InvalidParameterFormatDialog.jsx";
import { addRecord, handleMoveDown, handleMoveUp } from "../../DatasetUtils.js";
import { datasetData } from "../../NewDatasetSignals.jsx";
import {
  exportToCsvFile,
  hasSecondColumnWithKeyword,
  transformCsvRows,
  validateCsvFormat,
} from "../CsvUtilities/CsvHelper.js";
import { ReadCSV } from "../CsvUtilities/ReadCSV.js";
import { DeleteCell, ParameterValueCell, ValueCell } from "./CustomCells.js";
import { showFileUpload, showInvalidFormatDialog } from "./ParameterSignals.jsx";

export const ParameterGrid = ({ onChange }) => {
  const anchor = React.useRef(null);

  const updateListData = (newListData) => {
    onChange(newListData);
  };

  const itemChange = (event) => {
    const inEditID = event.dataItem.id;
    const field = event.field || "";
    const newData = datasetData.value.values.map((item) =>
      item.index === inEditID
        ? {
            ...item,
            [field]: event.value,
          }
        : item
    );
    updateListData(newData);
  };

  const uploadCSV = () => {
    showFileUpload.value = !showFileUpload.value;
  };

  const exportToCSV = () => {
    exportToCsvFile({
      values: datasetData.value.values,
      datasetName: datasetData.value.datasetName,
      headers: ["Parameter Name", "Parameter Value"],
      hasSecondColumn: true,
      secondColumnKey: "filterValue",
    });
  };

  const handleFileChange = async (e) => {
    if (e.target.files?.[0]) {
      const csvResult = await ReadCSV(e.target.files[0]);

      // Validate CSV format for Global Parameters
      const validation = validateCsvFormat(csvResult, "parameter");
      if (!validation.isValid) {
        showFileUpload.value = false;
        showInvalidFormatDialog.value = true;
        return;
      }

      // Transform rows to dataset format
      const hasParameterValueColumn = hasSecondColumnWithKeyword(csvResult.headers, "parameter");
      const csvData = transformCsvRows(csvResult.rows, hasParameterValueColumn, "filterValue");

      updateListData(csvData);
    }
    showFileUpload.value = false;
  };

  const closeInvalidFormatDialog = () => {
    showInvalidFormatDialog.value = false;
  };

  return (
    <>
      {showInvalidFormatDialog.value && (
        <InvalidParameterFormatDialog handleClose={closeInvalidFormatDialog} />
      )}
      <Grid
        data={datasetData.value.values}
        onItemChange={(e) => itemChange(e)}
      >
        <GridToolbar>
          <div style={{ display: "flex", alignItems: "center", gap: "5px", flexWrap: "wrap" }}>
            <button
              title="Add new"
              type="button"
              className="k-button k-button-md k-rounded-md k-button-solid k-button-solid-primary"
              onClick={() =>
                addRecord(datasetData.value.values, updateListData, {
                  value: "",
                  filterValue: "",
                })
              }
            >
              Add
            </button>
            <button
              title="Import Data"
              type="button"
              className="k-button k-button-md k-rounded-md k-button-solid k-button-solid-primary import-button"
              onClick={uploadCSV}
              ref={anchor}
            >
              {showFileUpload.value ? "Cancel" : "Import Data"}
            </button>
            <button
              title="Export Data"
              type="button"
              className="k-button k-button-md k-rounded-md k-button-solid k-button-solid-primary"
              onClick={exportToCSV}
            >
              Export Data
            </button>
            <Popup
              anchor={anchor.current}
              show={showFileUpload.value}
              popupClass={"popup-content"}
            >
              <div className="upload-section">
                <h3>Upload CSV File:</h3>
                <input
                  className="chooseFile"
                  type="file"
                  accept=".csv"
                  onChange={handleFileChange}
                />
              </div>
            </Popup>
          </div>
        </GridToolbar>
        <GridColumn
          width={80}
          title={null}
          cells={{
            data: renderMoveCell,
          }}
        />
        <GridColumn
          width="350px"
          field="value"
          title="Parameter Name"
          cells={{
            data: renderValueCell,
          }}
        />
        <GridColumn
          width="350px"
          field="filterValue"
          title="Parameter Value"
          cells={{
            data: renderParamValueCell,
          }}
        />
        <GridColumn
          field="delete"
          title="Delete"
          cells={{
            data: renderDeleteCell,
          }}
        />
      </Grid>
    </>
  );

  function renderMoveCell(props) {
    return (
      <td>
        <ButtonGroup>
          <Button
            className="!k-px-2"
            onClick={() => handleMoveUp(props, datasetData.value.values, updateListData)}
          >
            <FontAwesomeIcon
              icon={faArrowUpFromSquare}
              style={{
                "--fa-primary-color": "#0078D4",
                "--fa-secondary-color": "#919191",
                "--fa-primary-opacity": "1",
                "--fa-secondary-opacity": "1",
                fontSize: 14,
              }}
            />
          </Button>
          <Button
            className="!k-px-2"
            onClick={() => handleMoveDown(props, datasetData.value.values, updateListData)}
          >
            <FontAwesomeIcon
              icon={faArrowDownToSquare}
              style={{
                "--fa-primary-color": "#0078D4",
                "--fa-secondary-color": "#919191",
                "--fa-primary-opacity": "1",
                "--fa-secondary-opacity": "1",
                fontSize: 14,
              }}
            />
          </Button>
        </ButtonGroup>
      </td>
    );
  }

  function renderDeleteCell(props) {
    return (
      <DeleteCell
        {...props}
        data={datasetData.value.values}
        setData={updateListData}
      />
    );
  }

  function renderParamValueCell(props) {
    return (
      <ParameterValueCell
        {...props}
        data={datasetData.value.values}
        setData={updateListData}
      />
    );
  }
  function renderValueCell(props) {
    return (
      <ValueCell
        {...props}
        data={datasetData.value.values}
        setData={updateListData}
      />
    );
  }
};
